import type {
  CelebrationItem,
  CelebrationRoutingContext,
  CelebrationRoutingResult,
  EnrichedCelebration,
} from './celebration.types';
import { resolveCreatorCelebrationMeta } from './celebration.policy.creator';
import {
  resolveSellerCelebrationMeta,
  resolveSellerNextMilestone,
} from './celebration.policy.seller';
import {
  addDeferredCelebrationIds,
  canShowBlockingCelebration,
  readDeferredCelebrationIds,
} from './celebration.session';

const STALE_MS = 24 * 60 * 60 * 1000;
const COLD_LOAD_PATHS = [
  '/seller/dashboard',
  '/seller',
  '/seller/earnings',
  '/seller/orders',
  '/creator/dashboard',
  '/creator',
  '/creator/earnings',
];

function celebrationId(item: CelebrationItem): string {
  return item.id ?? item.code;
}

function isStale(unlockedAt?: string): boolean {
  if (!unlockedAt) return false;
  const ts = new Date(unlockedAt).getTime();
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts > STALE_MS;
}

function isColdLoadPath(pathname: string): boolean {
  return COLD_LOAD_PATHS.some((p) => pathname === p || pathname === `${p}/`);
}

function enrich(
  item: CelebrationItem,
  ctx: CelebrationRoutingContext,
  lockedForNext?: Array<{ title: string; description: string; href: string }> | null,
): EnrichedCelebration {
  if (ctx.persona === 'creator') {
    return { ...item, id: celebrationId(item), ...resolveCreatorCelebrationMeta(item) };
  }
  const next = resolveSellerNextMilestone(lockedForNext);
  return { ...item, id: celebrationId(item), ...resolveSellerCelebrationMeta(item, next) };
}

function shouldDeferBlocking(
  item: EnrichedCelebration,
  ctx: CelebrationRoutingContext,
): boolean {
  if (ctx.surface === 'progress' || ctx.surface === 'wizard' || ctx.surface === 'onboarding') {
    return false;
  }
  if (ctx.trigger === 'immediate') {
    return false;
  }
  if (ctx.surface === 'dashboard' && isColdLoadPath(ctx.pathname)) {
    return true;
  }
  if (isStale(item.unlockedAt)) {
    return true;
  }
  return false;
}

function batchTier1Toasts(items: EnrichedCelebration[]): EnrichedCelebration[] {
  if (items.length <= 3) return items;
  const sourceIds = items.map((i) => i.id);
  const first = items[0];
  return [
    {
      ...first,
      id: `batch-tier1-${sourceIds.join('-')}`,
      sourceIds,
      title: `${items.length} milestones unlocked`,
      description: items.map((i) => i.title).join(' · '),
      tier: 1,
      eyebrow: 'Recent wins',
      whyItMatters: 'Small wins add up — review them anytime on your progress page.',
      primaryCtaLabel: first.primaryCtaLabel,
      primaryCtaHref: first.primaryCtaHref,
      secondaryCtaLabel: first.secondaryCtaLabel,
    },
  ];
}

export function routeCelebrations(
  items: CelebrationItem[],
  ctx: CelebrationRoutingContext,
  options?: {
    lockedAchievements?: Array<{ title: string; description: string; href: string }> | null;
    includeDeferred?: boolean;
  },
): CelebrationRoutingResult {
  const deferredIds = new Set(readDeferredCelebrationIds(ctx.persona));
  const enriched = items
    .map((item) => enrich(item, ctx, options?.lockedAchievements))
    .filter((item) => options?.includeDeferred || !deferredIds.has(item.id));

  const tier1: EnrichedCelebration[] = [];
  const tier2: EnrichedCelebration[] = [];
  const tier3: EnrichedCelebration[] = [];
  const deferred: EnrichedCelebration[] = [];

  for (const item of enriched) {
    if (item.tier === 1) {
      tier1.push(item);
      continue;
    }
    if (shouldDeferBlocking(item, ctx)) {
      deferred.push(item);
      continue;
    }
    if (item.tier === 2) tier2.push(item);
    else tier3.push(item);
  }

  if (deferred.length > 0) {
    addDeferredCelebrationIds(ctx.persona, deferred.map((d) => d.id));
  }

  const toasts = batchTier1Toasts(tier1);

  let sheets = [...tier2];
  let modals = [...tier3];
  let batchModal: EnrichedCelebration[] | null = null;

  const blockingAllowed = ctx.trigger === 'immediate' || canShowBlockingCelebration();

  if (!blockingAllowed && (sheets.length > 0 || modals.length > 0)) {
    const overflow = [...sheets, ...modals];
    deferred.push(...overflow);
    addDeferredCelebrationIds(ctx.persona, overflow.map((d) => d.id));
    sheets = [];
    modals = [];
  }

  if (modals.length > 1) {
    batchModal = modals;
    modals = [];
  }

  if (sheets.length > 1) {
    sheets = [sheets[0]];
    const rest = tier2.slice(1);
    deferred.push(...rest);
    addDeferredCelebrationIds(ctx.persona, rest.map((d) => d.id));
  }

  return { toasts, sheets, modals, batchModal, deferred };
}

export function resolveSurface(pathname: string): CelebrationRoutingContext['surface'] {
  if (pathname.includes('/listing-wizard') || pathname.includes('/design-tool')) return 'wizard';
  if (pathname.includes('/onboarding')) return 'onboarding';
  if (pathname.includes('/progress')) return 'progress';
  if (pathname.includes('/settings')) return 'settings';
  if (pathname.endsWith('/dashboard') || pathname === '/seller' || pathname === '/creator') {
    return 'dashboard';
  }
  return 'default';
}
